import { isValidPhoneNumber as isValidNumberByMaxMetadata, parsePhoneNumber as parseNumberByMaxMetadata } from "libphonenumber-js/max";

/**
 * 전화번호 검증 헬퍼.
 *
 * 다른 유틸을 import 하지 않는다. (단위 테스트에서 라우팅/페이지 모듈까지 끌고 오지 않도록)
 *
 * ## 왜 "libphonenumber-js" 기본 엔트리를 쓰지 않는가
 *
 * 기본 엔트리는 min 메타데이터라 KR 에 대해 사실상 자릿수만 검사한다. 그래서
 * 유선 번호를 휴대폰 필드에 넣는 등 타입 오입력을 걸러내지 못한다.
 *
 * 타입별 패턴이 담긴 "/max" 메타데이터를 써야 백엔드와 같은 판정이 된다.
 * "/mobile" 메타데이터로도 휴대폰 판정은 가능하지만 "/max" + getType() 과 결과가 완전히 같아
 * (KR 15,046 케이스 브루트포스 비교에서 불일치 0건) 메타데이터 한 세대를 덜 싣기 위해 "/max" 만 쓴다.
 *
 * ## 버전 커플링 주의 (BE 와 세대를 맞춘다)
 *
 * 이 판정은 libphonenumber-js 에 내장된 메타데이터 세대에 의존한다. 핵심 요구사항은
 * "특정 번호를 막는 것" 이 아니라 **BE 와 FE 가 동일한 메타데이터 세대로 관리되는 것** 이다. (QA-3842)
 *
 * 현재는 백엔드 google libphonenumber 9.0.40 에 맞춰 libphonenumber-js **1.13.14** 로 고정한다.
 * (1.13.14 가 google 9.0.40 메타데이터를 싣는다 - CHANGELOG 기준)
 *
 * 과거 8.12.46 세대는 010-0 대역을 거부했지만, 구글은 8.13.17 에서 이를 허용
 * (0[1-46-9] -> 0[0-46-9])으로 바꿨고 9.0.40 도 허용한다. 따라서 이제 "010-0123-4567" 은
 * BE/FE 양쪽에서 유효로 판정된다. 이는 두 라이브러리 세대가 일치하므로 의도된 동작이다.
 *
 * package.json 은 재드리프트 방지를 위해 캐럿 없이 정확히 "1.13.14" 로 핀한다. BE 세대가 바뀌면
 * 대응하는 js 버전으로 함께 올리고 phone-util.test.ts 를 로컬에서 반드시 돌려 확인할 것.
 * (CI 에는 아직 테스트 단계가 없다.)
 */

/**
 * 휴대폰 번호 유효성 실패 시 공통 에러 메세지.
 *
 * 유선 번호를 휴대폰 필드에 넣거나, 자릿수는 맞지만 실제로 할당되지 않은 대역("010-5970-1234" 등)이
 * 주된 실패 케이스라 "형식에 맞게 입력" 보다 "유효하지 않은 번호" 가 정확하다.
 */
export const INVALID_PHONE_NUMBER_MESSAGE = "유효하지 않은 번호입니다";

/**
 * 한국 전화번호 여부 (휴대폰 / 유선 / 인터넷전화 / 대표번호 등 모든 유형 허용).
 */
export const isValidKoreanPhone = (phone?: string): boolean => {
  if (!phone) return false;
  return isValidNumberByMaxMetadata(phone, "KR");
};

/**
 * 한국 전화번호의 유형. 유효하지 않으면 undefined.
 */
const getKoreanPhoneType = (phone?: string): string | undefined => {
  if (!isValidKoreanPhone(phone)) return undefined;
  return parseNumberByMaxMetadata(phone as string, "KR").getType();
};

/**
 * 한국 휴대폰 번호 여부.
 *
 * 유선/인터넷전화(070)/대표번호(1588)는 모두 false 다.
 *
 * ## 010 접두로 좁히지 않는 이유 (의도된 선택)
 *
 * libphonenumber 의 KR mobile 패턴은 010 외에 011/016~019(레거시 01X)와 012(무선데이터)도
 * 유효로 본다. 교체 전 정규식들은 대부분 010 만 허용했으므로 이 함수는 그보다 넓다.
 *
 * 백엔드가 `isValidNumber()` 로 판정하므로 BE 도 이들을 통과시킨다. FE 를 더 좁히면
 * FE 에서만 막히는 불일치가 생기므로, BE 와 같은 기준을 유지하기로 결정했다.
 * 좁히고 싶으면 이 함수에 접두 조건을 추가하고 phone-util.test.ts 의 해당 케이스를 고칠 것.
 */
export const isValidKoreanMobile = (phone?: string): boolean => {
  return getKoreanPhoneType(phone) === "MOBILE";
};

/**
 * 휴대폰이 아닌 한국 전화번호 여부.
 *
 * 개인 휴대폰 사용이 금지된 필드(프로바이더 "문의 접수 전화번호" 등)에 사용한다.
 * 국제 표기("+82 10...")로 휴대폰을 우회 입력하는 것도 함께 차단된다.
 *
 * ## 안심번호(0507 등)를 허용하는 이유 (의도된 선택)
 *
 * 0507/0505/050x 는 getType() 이 PERSONAL_NUMBER 이며 실질적으로 개인 휴대폰으로 착신 전환된다.
 * 그래서 "개인 휴대폰 금지" 요구사항을 우회하는 수단으로 쓸 수 있지만, 반대로 개인 번호를
 * 노출하지 않으려는 정당한 수단이기도 해서 허용하기로 결정했다.
 * 막아야 하면 PERSONAL_NUMBER 도 제외하고 phone-util.test.ts 의 해당 케이스를 고칠 것.
 */
export const isValidKoreanNonMobilePhone = (phone?: string): boolean => {
  const type = getKoreanPhoneType(phone);
  return type !== undefined && type !== "MOBILE";
};

/**
 * 휴대폰 번호 validation (react-hook-form `validate` 용)
 *
 * @param mobileNumber 입력한 휴대폰 번호
 * @param requiredMessage 필수입력 에러 메세지 (없으면 "필수 입력 항목입니다")
 * @param formatMessage 유효성 에러 메세지 (없으면 INVALID_PHONE_NUMBER_MESSAGE)
 * @returns 유효하면 true, 아니면 에러 메세지
 */
export const validateMobileNumber = (mobileNumber?: string, requiredMessage?: string, formatMessage?: string): boolean | string => {
  if (!mobileNumber) return requiredMessage || "필수 입력 항목입니다";
  return isValidKoreanMobile(mobileNumber) || formatMessage || INVALID_PHONE_NUMBER_MESSAGE;
};
